import { useTranslation } from 'react-i18next';
// API Hook for making API calls with error handling - CORREGIDO COMPLETO
import { useState, useEffect, useCallback, useRef } from 'react';
import { handleApiError } from '../services/api';

// Generic hook for API calls
export const useApi = (apiFunction, dependencies = []) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFunction(...args);
      setData(response.data);
      return { success: true, data: response.data };
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError);
      return apiError;
    } finally {
      setLoading(false);
    }
  }, dependencies);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    reset
  };
};

// Hook for API calls that should execute immediately - ENHANCED
export const useApiCall = (apiFunction, dependencies = [], immediate = true) => {
  const { t } = useTranslation();
  const { data, loading, error, execute, reset } = useApi(apiFunction, dependencies);
  const [lastRefresh, setLastRefresh] = useState(null);
  const mountedRef = useRef(true);

  // Enhanced refetch that tracks when it was called
  const refetch = useCallback(async (...args) => {
    if (!mountedRef.current) return;

    try {
      const result = await execute(...args);
      if (mountedRef.current) {
        setLastRefresh(new Date());
      }
      return result;
    } catch (err) {
      console.error('Error in refetch:', err);
      return { error: true, message: err.message };
    }
  }, [execute]);

  // Initial execution
  useEffect(() => {
    if (immediate && apiFunction) {
      refetch();
    }
  }, [refetch, immediate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
    reset,
    lastRefresh
  };
};

// Hook for mutations (POST, PUT, DELETE) - ENHANCED
export const useMutation = (apiFunction, options = {}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [data, setData] = useState(null);

  const mutate = useCallback(async (...args) => {
    try {
      setLoading(true);
      setError(null);
      setIsSuccess(false);

      const response = await apiFunction(...args);
      if (response && response.success === false) {
        const apiError = {
          error: true,
          message: response.error || t('hardcoded.hooks_useapi.request_failed')
        };
        setError(apiError);
        setIsSuccess(false);

        if (options.onError) {
          options.onError(apiError);
        }

        return apiError;
      }

      setData(response?.data);
      setIsSuccess(true);

      if (options.onSuccess) {
        options.onSuccess(response?.data);
      }

      return { success: true, data: response?.data };
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError);
      setIsSuccess(false);

      if (options.onError) {
        options.onError(apiError);
      }

      return apiError;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, options]);

  const reset = useCallback(() => {
    setError(null);
    setLoading(false);
    setIsSuccess(false);
    setData(null);
  }, []);

  return {
    mutate,
    loading,
    error,
    isSuccess,
    data,
    reset
  };
};

// Hook for paginated API calls - ENHANCED
export const usePaginatedApi = (apiFunction, initialParams = {}) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...initialParams
  });

  const fetchData = useCallback(async (newParams = {}) => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = { ...params, ...newParams };
      const response = await apiFunction(queryParams);

      // Assuming API returns { data: [], total: number } format
      if (response.data) {
        if (Array.isArray(response.data)) {
          setData(response.data);
          setTotalCount(response.data.length);
        } else {
          setData(response.data.items || response.data.data || []);
          setTotalCount(response.data.total || 0);
        }
      }

      return { success: true, data: response.data };
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError);
      return apiError;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, params]);

  const nextPage = useCallback(() => {
    const newParams = { ...params, page: params.page + 1 };
    setParams(newParams);
    return fetchData(newParams);
  }, [params, fetchData]);

  const prevPage = useCallback(() => {
    const newParams = { ...params, page: Math.max(1, params.page - 1) };
    setParams(newParams);
    return fetchData(newParams);
  }, [params, fetchData]);

  const goToPage = useCallback((page) => {
    const newParams = { ...params, page };
    setParams(newParams);
    return fetchData(newParams);
  }, [params, fetchData]);

  const changePageSize = useCallback((limit) => {
    const newParams = { ...params, limit, page: 1 };
    setParams(newParams);
    return fetchData(newParams);
  }, [params, fetchData]);

  const updateParams = useCallback((newParams) => {
    const updatedParams = { ...params, ...newParams, page: 1 };
    setParams(updatedParams);
    return fetchData(updatedParams);
  }, [params, fetchData]);

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    totalCount,
    loading,
    error,
    params,
    fetchData,
    nextPage,
    prevPage,
    goToPage,
    changePageSize,
    updateParams,
    refetch: () => fetchData(params),
    hasNextPage: params.page * params.limit < totalCount,
    hasPrevPage: params.page > 1,
    totalPages: Math.ceil(totalCount / params.limit)
  };
};

// Hook for real-time data updates - ENHANCED
export const useRealtimeData = (apiFunction, interval = 30000, dependencies = []) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (!mountedRef.current || !isActive) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiFunction();
      if (mountedRef.current) {
        setData(response.data);
        setLastUpdated(new Date());
      }

      return { success: true, data: response.data };
    } catch (err) {
      if (mountedRef.current) {
        const apiError = handleApiError(err);
        setError(apiError);
      }
      return handleApiError(err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiFunction, isActive, ...dependencies]);

  // Start real-time updates
  const start = useCallback(() => {
    setIsActive(true);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Initial fetch
    fetchData();

    // Set up interval
    intervalRef.current = setInterval(fetchData, interval);
  }, [fetchData, interval]);

  // Stop real-time updates
  const stop = useCallback(() => {
    setIsActive(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Toggle real-time updates
  const toggle = useCallback(() => {
    if (isActive) {
      stop();
    } else {
      start();
    }
  }, [isActive, start, stop]);

  useEffect(() => {
    start();

    // Cleanup
    return () => {
      mountedRef.current = false;
      stop();
    };
  }, []);

  return {
    data,
    loading,
    error,
    lastUpdated,
    isActive,
    refetch: fetchData,
    start,
    stop,
    toggle
  };
};

// Hook for file uploads - ENHANCED
export const useFileUpload = (uploadFunction, options = {}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  const upload = useCallback(async (file, additionalData = {}) => {
    try {
      setLoading(true);
      setError(null);
      setProgress(0);
      setIsSuccess(false);

      const formData = new FormData();
      formData.append(t('hardcoded.components_common_imageupload.file'), file);

      // Add additional data to form
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      const response = await uploadFunction(formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(progress);
        }
      });

      setUploadedFile(file);
      setIsSuccess(true);

      if (options.onSuccess) {
        options.onSuccess(response.data, file);
      }

      return { success: true, data: response.data };
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError);
      setIsSuccess(false);

      if (options.onError) {
        options.onError(apiError, file);
      }

      return apiError;
    } finally {
      setLoading(false);
      setProgress(0);
    }
  }, [uploadFunction, options]);

  const reset = useCallback(() => {
    setError(null);
    setLoading(false);
    setProgress(0);
    setIsSuccess(false);
    setUploadedFile(null);
  }, []);

  return {
    upload,
    loading,
    progress,
    error,
    isSuccess,
    uploadedFile,
    reset
  };
};

// Hook for optimistic updates - ENHANCED
export const useOptimisticUpdate = (apiFunction, updateFunction) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOptimistic, setIsOptimistic] = useState(false);

  const execute = useCallback(async (newData, ...args) => {
    try {
      setLoading(true);
      setError(null);

      // Optimistically update the UI
      const previousData = data;
      setIsOptimistic(true);
      setData(updateFunction(data, newData));

      try {
        const response = await apiFunction(...args);
        setData(response.data);
        setIsOptimistic(false);
        return { success: true, data: response.data };
      } catch (err) {
        // Revert optimistic update on error
        setData(previousData);
        setIsOptimistic(false);
        throw err;
      }
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError);
      return apiError;
    } finally {
      setLoading(false);
    }
  }, [apiFunction, updateFunction, data]);

  return {
    data,
    loading,
    error,
    isOptimistic,
    execute,
    setData
  };
};

// 🚨 NEW: Hook for telemetry data with date range support
export const useTelemetryData = (vehicleId, initialDateRange = null, options = {}) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [positions, setPositions] = useState(['all']);
  const [refreshInterval, setRefreshInterval] = useState(options.refreshInterval || 30000);

  const { data, loading, error, refetch, lastRefresh } = useApiCall(() => {
    if (!vehicleId) return null;

    const params = {
      vehicle_id: vehicleId,
      limit: options.limit || 10000
    };

    if (dateRange && dateRange.startDate && dateRange.endDate) {
      params.start_date = dateRange.startDate;
      params.end_date = dateRange.endDate;
    }

    if (positions && !positions.includes('all')) {
      params.positions = positions.join(',');
    }

    return options.apiFunction ? options.apiFunction(params) : null;
  }, [vehicleId, dateRange?.startDate, dateRange?.endDate, positions.join(',')]);

  // Auto-refresh capability
  const { start: startAutoRefresh, stop: stopAutoRefresh, isActive: isAutoRefreshActive } = useRealtimeData(
    () => refetch(),
    refreshInterval,
    [vehicleId, dateRange, positions]
  );

  const updateDateRange = useCallback((newDateRange) => {
    setDateRange(newDateRange);
  }, []);

  const updatePositions = useCallback((newPositions) => {
    setPositions(newPositions);
  }, []);

  const updateRefreshInterval = useCallback((newInterval) => {
    setRefreshInterval(newInterval);
    if (isAutoRefreshActive) {
      stopAutoRefresh();
      // Restart with new interval
      setTimeout(() => startAutoRefresh(), 100);
    }
  }, [isAutoRefreshActive, startAutoRefresh, stopAutoRefresh]);

  return {
    data,
    loading,
    error,
    refetch,
    lastRefresh,
    dateRange,
    updateDateRange,
    positions,
    updatePositions,
    refreshInterval,
    updateRefreshInterval,
    isAutoRefreshActive,
    startAutoRefresh,
    stopAutoRefresh
  };
};

const PRESET_KEYS = {
  DAY1: '1day',
  WEEK1: '1week',
  MONTH1: '1month',
  MONTH6: '6months',
  YEAR1: '1year',
};

const PRESET_TRANSLATION_KEYS = {
  [PRESET_KEYS.DAY1]: 'hardcoded.components_schematics_vehicletelemetrymodal.1day',
  [PRESET_KEYS.WEEK1]: 'hardcoded.components_schematics_vehicletelemetrymodal.1week',
  [PRESET_KEYS.MONTH1]: 'hardcoded.components_schematics_vehicletelemetrymodal.1month',
  [PRESET_KEYS.MONTH6]: 'hardcoded.components_schematics_vehicletelemetrymodal.6months',
  [PRESET_KEYS.YEAR1]: 'hardcoded.components_schematics_vehicletelemetrymodal.1year',
};

// 🚨 NEW: Hook for date range management
export const useDateRange = (initialRange = null) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState(initialRange || {
    startDate: '',
    endDate: '',
    enabled: false
  });
  const [selectedPreset, setSelectedPreset] = useState(PRESET_KEYS.DAY1);

  const getPresetLabel = useCallback((presetKey) => {
    const translationKey = PRESET_TRANSLATION_KEYS[presetKey];
    return translationKey ? t(translationKey) : presetKey;
  }, [t]);

  const updateDateRange = useCallback((newRange) => {
    setDateRange(prev => ({ ...prev, ...newRange }));
  }, []);

  const setPreset = useCallback((preset) => {
    const now = new Date();
    const endDate = new Date(now);
    let startDate = new Date(now);

    switch (preset) {
      case PRESET_KEYS.DAY1:
        startDate.setDate(now.getDate() - 1);
        break;
      case PRESET_KEYS.WEEK1:
        startDate.setDate(now.getDate() - 7);
        break;
      case PRESET_KEYS.MONTH1:
        startDate.setMonth(now.getMonth() - 1);
        break;
      case PRESET_KEYS.MONTH6:
        startDate.setMonth(now.getMonth() - 6);
        break;
      case PRESET_KEYS.YEAR1:
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setDate(now.getDate() - 1);
    }

    setSelectedPreset(preset);
    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      enabled: true
    });
  }, []);

  const clearRange = useCallback(() => {
    setDateRange({
      startDate: '',
      endDate: '',
      enabled: false
    });
    setSelectedPreset(PRESET_KEYS.DAY1);
  }, []);

  return {
    dateRange,
    selectedPreset,
    selectedPresetLabel: getPresetLabel(selectedPreset),
    getPresetLabel,
    updateDateRange,
    setPreset,
    clearRange,
    isValid: dateRange.startDate && dateRange.endDate && dateRange.enabled
  };
};

// 🚨 NEW: Hook for chart data processing
export const useChartData = (rawData, processingOptions = {}) => {
  const { t } = useTranslation();
  const [processedData, setProcessedData] = useState([]);
  const [processing, setProcessing] = useState(false);

  const processData = useCallback(async () => {
    if (!rawData || !Array.isArray(rawData)) {
      setProcessedData([]);
      return;
    }

    setProcessing(true);

    try {
      // Process data based on options
      let processed = [...rawData];

      // Apply filters if specified
      if (processingOptions.positions && !processingOptions.positions.includes('all')) {
        processed = processed.filter(item =>
          processingOptions.positions.includes(item.position)
        );
      }

      if (processingOptions.metricType) {
        processed = processed.filter(item => {
          if (processingOptions.metricType === 'acceleration') {
            return item.type === 'ACC';
          } else {
            return item.type === 'PSI';
          }
        });
      }

      // Sort by timestamp
      processed.sort((a, b) => new Date(a.time_stamp || a.created_at) - new Date(b.time_stamp || b.created_at));

      setProcessedData(processed);
    } catch (error) {
      console.error('Error processing chart data:', error);
      setProcessedData([]);
    } finally {
      setProcessing(false);
    }
  }, [rawData, processingOptions.positions, processingOptions.metricType]);

  useEffect(() => {
    processData();
  }, [processData]);

  return {
    processedData,
    processing,
    reprocess: processData
  };
};